import React, { Fragment, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { FILTER_POSTAL_CODE, PAGE } from 'dpl/constants/query_params';
import { RESULTS_TYPES } from 'dpl/puppy_search_results/utils/constants';
import { resultGroupViewProps } from 'dpl/puppy_search_results/components/SearchResults';
import { makeSequence } from 'dpl/shared/utils';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import FartherBreedersBanner from 'dpl/puppy_search_results/components/FartherBreedersBanner';
import SkeletonBlock from 'dpl/common/design_system/SkeletonBlock';

import Card from './Card';

const RESULT_GROUP_SIZE = 4;

const EXPERIMENT_BREEDER_CARD_PHOTO_SIZE = isLessThanBreakpoint('lg')
  ? '240px'
  : '260px';

export function CardlessSearchResultsSkeleton({ resultsPerPage }) {
  return (
    <div className="SearchResultsSkeleton">
      <ul className="flex flex-column gap-8">
        {makeSequence(resultsPerPage).map(index => (
          <li key={index}>
            <div className="CardlessSearchResultsSkeleton bg-white overflow-hidden">
              <div className="flex items-center gap-12 md:gap-16 pt4 ph4 mb6">
                <SkeletonBlock className="br-100" height="46px" width="46px" />
                <div>
                  <SkeletonBlock
                    className="font-18 md:font-20 mb2"
                    height="1em"
                    width="10em"
                  />
                  <SkeletonBlock
                    className="font-12"
                    height="1em"
                    width="16em"
                  />
                </div>
              </div>
              <div className="flex gap-8 overflow-hidden pl4">
                {makeSequence(2).map(idx => (
                  <SkeletonBlock
                    key={idx}
                    className="br3 w-100 flex-none"
                    height={EXPERIMENT_BREEDER_CARD_PHOTO_SIZE}
                    width={EXPERIMENT_BREEDER_CARD_PHOTO_SIZE}
                  />
                ))}
              </div>
              <div className="flex flex-column gap-8 mh4 mt4 pb4">
                <SkeletonBlock className="font-14" height="1em" width="100%" />
                <SkeletonBlock className="font-14" height="1em" width="50%" />
              </div>
              <div className="flex justify-between mh4 pt3 pb4 items-center f2 bt b--default">
                <SkeletonBlock className="font-14" height="1em" width="8em" />
                <SkeletonBlock className="font-14" height="1em" width="6em" />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

CardlessSearchResultsSkeleton.propTypes = {
  resultsPerPage: PropTypes.number.isRequired
};

function SearchResultsItem({
  result,
  results,
  resultsType,
  position,
  positionOnPage,
  analyticsPageNumber
}) {
  const { breedId } = useSelector(({ server }) => server);

  const analyticsView = ANALYTICS_VIEWS.BREEDER_SEARCH_RESULTS;
  const analyticsViewFeature = ANALYTICS_VIEW_FEATURES.BREEDER_CARD;

  const analytics = useMemo(
    () => ({
      page_number: analyticsPageNumber,
      position,
      view: analyticsView,
      view_feature_location: ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS
    }),
    [position, analyticsPageNumber]
  );

  const trackInView = useCallback(() => {
    if (positionOnPage % RESULT_GROUP_SIZE === 1) {
      const idx = results.indexOf(result);
      const resultGroup = results.slice(idx, idx + RESULT_GROUP_SIZE);

      sendRudderstackEvent(
        ANALYTICS_EVENTS.SEARCH_RESULT_GROUP_VIEWED_BY_USER,
        {
          view: analyticsView,
          view_feature: analyticsViewFeature,
          view_feature_location:
            ANALYTICS_VIEW_FEATURE_LOCATIONS.SEARCH_RESULTS,
          results: resultGroup.map((r, groupIdx) =>
            resultGroupViewProps(
              breedId,
              resultsType,
              r,
              position + groupIdx,
              analyticsPageNumber
            )
          )
        }
      );
    }
  }, [
    results,
    positionOnPage,
    analyticsView,
    analyticsViewFeature,
    position,
    analyticsPageNumber,
    breedId,
    resultsType
  ]);

  return (
    <Card profile={result} analytics={analytics} trackInView={trackInView} />
  );
}

SearchResultsItem.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  results: PropTypes.array.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  result: PropTypes.object.isRequired,
  resultsType: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  positionOnPage: PropTypes.number.isRequired,
  analyticsPageNumber: PropTypes.number.isRequired
};

export default function CardlessSearchResults({
  analyticsPageNumber,
  distanceInMiles,
  results,
  totalItemCount
}) {
  const {
    itemsPerPage,
    stateLander,
    citySlug,
    preappliedFilters: { stateSlug } = {}
  } = useSelector(({ server }) => server);

  const params = useSelector(({ queryParams }) => queryParams);
  const currentPage = Number(params[PAGE]) || 1;
  const filteredByLocation = Boolean(params[FILTER_POSTAL_CODE] || stateSlug);

  const isFirstPage = currentPage === 1;

  function getPosition(idx) {
    let position = idx + 1;
    if (currentPage > 1) {
      position += (currentPage - 1) * itemsPerPage;
    }

    return position;
  }

  return (
    <ul className="SearchResults SearchResults--v2 flex flex-column gap-8">
      {results?.map((result, idx) => {
        const positionOnPage = idx + 1;

        const isFirstResult = idx === 0 && isFirstPage;
        const prevResult = results[idx - 1] || {};

        const didStartInRange =
          isFirstResult || prevResult.distance_in_miles <= distanceInMiles;

        const isInRange = result.distance_in_miles <= distanceInMiles;

        const didSwitchToOutOfRange =
          !stateLander && distanceInMiles && didStartInRange && !isInRange;

        const shouldDisplayFartherBreedersBanner = Boolean(
          filteredByLocation &&
            !result.snoozed &&
            !stateLander &&
            !citySlug &&
            distanceInMiles &&
            didSwitchToOutOfRange
        );

        const remainingResultsCount =
          totalItemCount - ((currentPage - 1) * itemsPerPage + idx);

        return (
          <Fragment key={result.id}>
            {shouldDisplayFartherBreedersBanner && (
              <li className="mb8 mb10-md">
                <FartherBreedersBanner count={remainingResultsCount} />
              </li>
            )}
            <li>
              <SearchResultsItem
                result={result}
                resultsType={RESULTS_TYPES.BREEDERS}
                results={results}
                position={getPosition(idx)}
                positionOnPage={positionOnPage}
                analyticsPageNumber={analyticsPageNumber}
              />
            </li>
          </Fragment>
        );
      })}
    </ul>
  );
}

CardlessSearchResults.propTypes = {
  results: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
  distanceInMiles: PropTypes.number,
  totalItemCount: PropTypes.number,
  analyticsPageNumber: PropTypes.number.isRequired
};

CardlessSearchResults.defaultProps = {
  distanceInMiles: null,
  totalItemCount: 0
};
